import { useCallback, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useLocationSearch } from '#src/app/pages/searchV2/shared/hooks';
import type { LocationItem } from '#src/app/pages/searchV2/shared/types';

type Result = ModelHookResult<
	{
		data: {
			isLoading: boolean;
			isError: boolean;
			locations: readonly LocationItem[];
			selectedKey: string;
		};
		searchTerm: string;
	},
	{
		handleInputFocus: () => void;
		handleInputChange: (value: string) => void;
		handleInputBlur: () => void;
		selectLocation: (searchValue: string) => void;
	}
>;

export function useLocationFilter(): Result {
	const track = useTracking();

	const {
		data: { locations, isLoading, isError, selectedLocation },
		actions: { searchDebounced, selectLocation },
	} = useLocationSearch();

	const [localSearchTerm, setLocalSearchTerm] = useState<string | null>(null);
	const searchTerm = localSearchTerm ?? (selectedLocation || '');

	return {
		data: {
			data: {
				isLoading,
				isError,
				locations,
				selectedKey: selectedLocation || '',
			},
			searchTerm,
		},
		actions: {
			handleInputFocus: useCallback(() => {
				void track('search_location_filter_enter');
			}, [track]),
			handleInputChange: useCallback(
				(value: string) => {
					if (!value) {
						setLocalSearchTerm('');
						return;
					}
					void track('search_location_filter_search', { search_term: value });
					searchDebounced(value);
					setLocalSearchTerm(value);
				},
				[searchDebounced, track],
			),
			handleInputBlur: useCallback(() => {
				setLocalSearchTerm(null);
			}, []),
			selectLocation: useCallback(
				(selectedKey: string) => {
					if (!selectedKey) {
						return;
					}
					const location = locations.find((loc) => loc.value === selectedKey);
					if (!location) return;
					selectLocation(location.value, location.type);
					setLocalSearchTerm(null);
				},
				[locations, selectLocation],
			),
		},
	};
}
